import {
  DeleteFilled,
  DeleteOutlined,
  EditFilled,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { DragSortingTable } from '../DraggableTable/DraggingTable';

import {
  Button,
  Card,
  Divider,
  Form,
  Image,
  Input,
  Modal,
  Popconfirm,
  Tag,
  DatePicker,
  Select
} from 'antd';
import { observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { rootContext } from 'stores';
import { ISubModel } from 'types';
import { EditTrims } from 'components/EditTrims/EditTrims';
import dayjs from 'dayjs';
import './Submodels.scss';

const Submodels: React.FC = observer(() => {
  const { RangePicker } = DatePicker;
  const currentYear = new Date().getFullYear();
  const {
    PartsStore: {
      state: { data, ui },
      setState,
      createSubModel,
      updateSubmodel,
      deleteSubModel,
      orderTrim
    },
    RegionStore: {
      getRegions,
      state: {
        data: { regions },
        ui: { getRegionsLoading }
      }
    }
  } = useContext(rootContext);
  useEffect(() => {
    getRegions();
  }, [getRegions]);

  const [file, setFile] = useState<File | null>(null);
  const [addSubModelForm] = Form.useForm();
  React.useEffect(() => {
    if (
      data?.subModelModal?.endMakingYear &&
      data?.subModelModal?.startMakingYear
    )
      addSubModelForm.setFieldsValue({
        region: data?.subModelModal?.region.map(region => region.id),
        makingYear: [
          dayjs(data?.subModelModal.startMakingYear, 'YYYY'),
          dayjs(data?.subModelModal.endMakingYear, 'YYYY')
        ]
      });
  }, [data?.subModelModal?.endMakingYear]);
  const validations = (rule: any, value: any) => {
    if (value && value?.trim()?.length === 0) {
      return Promise.reject('Whitespaces are not valid.');
    } else {
      return Promise.resolve(value?.trim() || '');
    }
  };
  return (
    <div className="SubmodelsStyles">
      <Modal
        title="Sub Models"
        width="90%"
        className="Submodel__modal__box"
        visible={data.subModelModal ? true : false}
        footer={false}
        onCancel={() => {
          setState({
            data: {
              ...data,
              subModelModal: null,
              selectedSubmodel: null
            }
          });
          addSubModelForm.resetFields(['name', 'file']);
        }}
      >
        <Form
          className="subModelForm"
          layout="inline"
          form={addSubModelForm}
          onFinish={async values => {
            if (values?.makingYear?.[0] && values?.makingYear?.[1]) {
              values.endMakingYear = dayjs(values.makingYear[1]).format('YYYY');
              values.startMakingYear = dayjs(values.makingYear[0]).format('YYYY');
            }
            delete values.makingYear;
            if (!data.selectedSubmodel && data.subModelModal?.id && file) {
              const response = await createSubModel({
                ...values,
                part_id: data.subModelModal.id,
                [values.name]: file
              });
              if (response) {
                setFile(null);
                addSubModelForm.resetFields(['name', 'file']);
              }
            } else if (!data.selectedSubmodel && data.subModelModal?.id) {
              const response = await createSubModel({
                ...values,
                part_id: data.subModelModal.id
              });
              if (response) {
                setFile(null);
                addSubModelForm.resetFields(['name', 'file']);
              }
            } else if (data.subModelModal?.id && data.selectedSubmodel?.id) {
              // return console.log(data.selectedSubmodel)
              const response = await updateSubmodel({
                ...values,
                id: data.selectedSubmodel?.id,
                part_id: data.subModelModal.id,
                ...(file
                  ? { [values.name]: file }
                  : { submodel_name: values.name })
              });
              if (response) {
                file && setFile(null);
                addSubModelForm.resetFields(['name', 'file']);
              }
            }
          }}
          onSubmitCapture={e => { }}
        >
          <Form.Item
            label="Submodel"
            name="name"
            rules={[
              { required: true, message: 'Submodel name is required' },
              { validator: validations }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Region"
            name="region"
            rules={[
              {
                required: Boolean(!data.selectedTrim),
                message: 'Region is required'
              }
            ]}
          >
            <Select
              tagRender={({ value }) => {
                return <Tag>{regions.find(rg => rg.id === value)?.name}</Tag>;
              }}
              style={{ width: '200px' }}
              loading={getRegionsLoading}
              placeholder="Select Region"
              mode="multiple"
              showSearch={false}
              dropdownRender={menu => (
                <div>
                  <Divider style={{ margin: '4px 0' }} />
                  {menu}
                </div>
              )}
            >
              {regions?.map(({ name, id }) => (
                <Select.Option key={id} value={id} className="regionOption">
                  <span>{name}</span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Image"
            name="file"
            tooltip={
              data.subModelModal?.model_image
                ? `Image will inherit model level image unless passed`
                : `Image is required since modal has no image specified`
            }
            rules={[
              {
                required: data.subModelModal?.model_image
                  ? false
                  : data.selectedSubmodel
                    ? false
                    : true,
                message: 'Image is required'
              }
            ]}
          >
            <Input
              onChange={e => e.target.files && setFile(e.target.files[0])}
              type="file"
              accept=".jpg,.png,.jpeg,.gif"
              className="upload__file"
            />
          </Form.Item>
          <Form.Item
            label="Making Year"
            name="makingYear"
            rules={[
              {
                required: Boolean(!data.selectedSubmodel),
                message: 'Please Enter Making Year'
              }
            ]}
          >
            <RangePicker
              format="YYYY"
              picker="year"
              placeholder={['Start Year', 'End Year']}
              disabledDate={date => date && date.year() > currentYear + 1}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={ui.addSubmodelLoading}
            >
              {data.selectedSubmodel ? 'Edit Submodel' : 'Add Submodel'}
            </Button>
          </Form.Item>
          {data.selectedSubmodel && (
            <Button
              type="primary"
              disabled={ui.addSubmodelLoading}
              onClick={() => {
                setState({
                  data: { ...data, selectedSubmodel: null }
                });
                addSubModelForm.resetFields(['name', 'file']);
              }}
            >
              Cancel Editing
            </Button>
          )}
        </Form>
        <Divider />
        <EditTrims />
        <Card>
          <DragSortingTable<ISubModel>
            draggedRow={(index, id) =>
              orderTrim({ index, id, type: 'SUBMODEL' })
            }
            className="subModelTable"
            rowKey={'id'}
            dataSource={
              data.subModelModal?.submodel ? data.subModelModal.submodel : []
            }
            pagination={false}
            loading={Boolean(ui.deleteSubModelLoading)}
            columns={[
              {
                dataIndex: 'submodel',
                title: 'Name',
                render: (name, row) => {
                  let selectedStyle = {
                    ...(row?.id === data?.selectedSubmodel?.id
                      ? {
                        colour: 'red',
                        fontWeight: 'bold'
                      }
                      : {})
                  };
                  // @ts-ignore
                  return <p style={selectedStyle}>{name}</p>;
                }
              },
              {
                title: 'Image',
                dataIndex: 'url',
                key: 'url',
                align: 'center',
                width: 10,
                render: value => (
                  <div className="tableImageLarge">
                    <Image className="tableImage" src={value} />
                  </div>
                )
              },
              {
                title: 'Regions',
                dataIndex: 'region',
                width: '20%',
                align: 'center',
                render: (value, row) => {
                  return !value?.length ? (
                    <Button>No Region</Button>
                  ) : (
                    value?.map((item: { name: string }) => (
                      <Tag>{item.name}</Tag>
                    ))
                  );
                }
              },
              {
                title: 'Start Year',
                dataIndex: 'startMakingYear',
                align: 'center',
                render: value => <p>{value ? value : '----'}</p>
              },
              {
                title: 'End Year',
                dataIndex: 'endMakingYear',
                align: 'center',
                render: value => <p>{value ? value : '----'}</p>
              },
              {
                title: 'Trims',
                dataIndex: 'trim',
                align: 'center',
                render: (value, row) => {
                  return !value ? (
                    <Button
                      onClick={() => {
                        setState({
                          data: {
                            ...data,
                            editTrim: row.id,
                            trims: [],
                            selectedSubmodel: row
                          }
                        });
                      }}
                    >
                      Add Trims
                    </Button>
                  ) : (
                    row?.trim?.map(item => (
                      <div
                        onClick={() => {
                          setState({
                            data: {
                              ...data,
                              selectedTrim: null,
                              editTrim: row.id,
                              trims: value,
                              selectedSubmodel: row
                            }
                          });
                        }}
                      >
                        <Tag>{item.name}</Tag>
                        <div className="tableImage">
                          <img src={item?.image} alt={item.name} />
                        </div>
                      </div>
                    ))
                  );
                }
              },
              {
                title: 'Action',
                width: 10,
                render: (_, row: ISubModel) => (
                  <div className="actionButtons">
                    <Button
                      ghost={true}
                      className="editButton"
                      disabled={ui.deleteSubModelLoading === row.id}
                      onClick={() => {
                        setState({ data: { ...data, selectedSubmodel: row } });
                        addSubModelForm.setFieldsValue({
                          name: row.submodel,
                          region: row?.region?.map(region => region.id) || [],
                          makingYear: [
                            dayjs(row?.startMakingYear || new Date().getFullYear(), 'YYYY'),
                            dayjs(row?.endMakingYear || new Date().getFullYear(), 'YYYY')
                          ]
                        });
                      }}
                    >
                      <EditFilled />
                    </Button>
                    <Popconfirm
                      onConfirm={e => {
                        data.subModelModal?.id &&
                          deleteSubModel({
                            id: row.id,
                            part_id: data.subModelModal?.id
                          });
                        setState({
                          data: { ...data, selectedSubmodel: null }
                        });
                        addSubModelForm.resetFields(['name', 'file']);
                      }}
                      onCancel={e => e?.stopPropagation()}
                      title={() => (
                        <p>
                          Delete this <b>Submodel</b> ?
                        </p>
                      )}
                      icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                    >
                      <Button
                        ghost={true}
                        className="deleteButton"
                        loading={ui.deleteSubModelLoading === row.id}
                      >
                        <DeleteFilled />
                      </Button>
                    </Popconfirm>
                  </div>
                )
              }
            ]}
          ></DragSortingTable>
        </Card>
      </Modal>
    </div>
  );
});

export default Submodels;
